import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchEducationMaster } from "../../../../store/OnboardingSlice/thunk";
import {
  normalizeEducationMasterItems,
  rankEducationMasterOptions,
  withEducationMasterCache,
} from "../../../../utils/educationHelpers";
import {
  extractOnboardingActionData,
  rejectMessageFromThunk,
} from "../../../../utils/onboardingHelpers";

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const useEducationMasterOptions = ({
  categories,
  search = "",
  enabled = true,
  minSearchChars = 0,
}) => {
  const dispatch = useDispatch();
  const debouncedSearch = useDebouncedValue(String(search || "").trim(), 300);
  const categoryKey = (categories || []).join(",");
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  const shouldSearch = debouncedSearch.length >= minSearchChars;
  const isQueryEnabled = enabled && categoryKey.length > 0 && shouldSearch;
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isQueryEnabled) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setErrorMessage("");

    const uniqueCategories = [...new Set(categoriesRef.current || [])];
    const cacheKey = `${uniqueCategories.slice().sort().join(",")}|${debouncedSearch}`;

    withEducationMasterCache(cacheKey, () =>
      Promise.all(
        uniqueCategories.map((category) =>
          dispatch(
            fetchEducationMaster({
              category,
              search: debouncedSearch || undefined,
            }),
          )
            .unwrap()
            .then((response) => {
              const data = extractOnboardingActionData(response);
              if (data == null) {
                throw new Error("Request failed");
              }
              return normalizeEducationMasterItems(data);
            }),
        ),
      ).then((lists) => rankEducationMasterOptions(lists.flat())),
    )
      .then((next) => {
        if (cancelled) return;
        setOptions(Array.isArray(next) ? next : []);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setOptions([]);
        setErrorMessage(
          rejectMessageFromThunk(error, "Unable to load suggestions."),
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isQueryEnabled, debouncedSearch, categoryKey]);

  return {
    options,
    isLoading: isQueryEnabled && isLoading,
    isError: Boolean(errorMessage),
    errorMessage: errorMessage || null,
    isSearchReady: shouldSearch,
  };
};

export default useEducationMasterOptions;
